<template>
  <modal :title="$t('Change Fee')" :displayed="displayed" @close="$emit('close')">
    <button slot="title-action" v-t type="button" form="feeForm" @click.prevent="submitForm">
      Save
    </button>
    <vue-form id="feeForm" slot="body" :state="formState" class="fee-form" @submit.prevent="submitForm">
      <p class="description">
        {{ clientFullName }} &nbsp;
        <span v-t>will get a notification email of any fee changes. The new fee will be effective the next billing period.</span>
      </p>
      <validate class="fee-field">
        <label for="fee">
          <span v-t>Planning Fee (Max</span>
          <span>&nbsp;{{ MAX_FEE }}</span>
          <span>%)</span>
        </label>
        <div class="fee-container input">
          <input id="fee" v-model.number="fee" name="fee" type="number" step="0.01" required
                 :class="{ invalid: inputError.fee.$invalid }"
                 :min="MIN_FEE" :max="MAX_FEE"
                 @blur="updateError('fee')"
                 @input="feeValue(fee)"
          >
          <span class="fee-percentage" :class="{ neg: inputError.fee.$invalid }">%</span>
        </div>
        <div v-if="inputError.fee.$error" class="error-message">
          <div v-if="inputError.fee.$error.required" v-t>Required</div>
          <div v-if="inputError.fee.$error.min || inputError.fee.$error.max" v-t>Fee must be 0% to 1%</div>
        </div>
      </validate>

      <hr class="section-separator">

      <section class="email-section">
        <div class="email-container">
          <label v-t for="message" :class="{ error: inputError.message }">Message from advisor (you):</label>
          <validate>
            <textarea
              id="message" v-model="message" name="message" class="email-content"
              :disabled="isTextAreaDisabled" required maxlength="1000" :class="{ error: inputError.message.$invalid }"
              @blur="updateError('message')"
            />
            <button type="button" class="subtle silver edit-message" @click="toggleTextArea">{{ getEditMessageButtonText }}</button>
            <div v-if="inputError.message.$error" class="error-message">
              <div v-if="inputError.message.$error.required" v-t>Required</div>
            </div>
          </validate>
        </div>
      </section>
      <input hidden type="submit" value="submit">
    </vue-form>
  </modal>
</template>

<script>
import { modal } from '@/components/layout';
import { mapActions, mapGetterMethods } from '@/lib/vue';
import filters from '@/lib/utils/filters';
import { maxFee, minFee } from '@/lib/utils/constants';
import { isClientPending } from '@/store/logic';
import errorStore from '@/store/error';

const _ = {
  errorStore,
};

export default {
  _,
  components: { modal },
  filters,
  props: {
    displayed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      client: null,
      MIN_FEE: minFee,
      MAX_FEE: maxFee,
      formState: {},
      inputError: {
        fee: {},
        message: {},
      },
      isTextAreaDisabled: true,
      fee: '',
      message: '',
    };
  },
  computed: {
    clientFullName() {
      if (!this.client) return '';

      const firstName = this.client.firstName || '';
      const lastName = this.client.lastName || '';
      return `${firstName} ${lastName}`;
    },
    getEditMessageButtonText() {
      if (this.isTextAreaDisabled) {
        return this.$t('Edit');
      }
      return this.$t('Discard Changes');
    },
  },
  watch: {
    displayed(visibility) {
      if (visibility === true) {
        this.client = this.getClient(this.$route.params.id);
        this.message = this.buildEmailMessage(this.client.firstName);
        this.fee = this.client.fee;
      }
    },
  },
  methods: {
    ...mapGetterMethods(['getClient']),
    ...mapActions(['updateFee']),

    buildEmailMessage(firstName) {
      return `${this.$t('Hi')} ${firstName !== undefined && firstName !== '' ? firstName : '[firstname]'},

${this.$t("I've updated your fee as we discussed. If you have any questions, let me know.")}`;
    },

    updateError(fieldName) {
      this.inputError[fieldName] = Object.assign({}, this.formState[fieldName]);
      this.inputError[fieldName].$error = Object.assign({}, this.formState[fieldName].$error);
    },

    updateAllErrors() {
      this.updateError('fee');
      this.updateError('message');
    },

    feeValue(number) {
      this.fee = parseFloat(number.toFixed(2));
    },

    toggleTextArea() {
      this.isTextAreaDisabled = !this.isTextAreaDisabled;
      if (this.isTextAreaDisabled) {
        this.message = this.buildEmailMessage(this.client.firstName);
        this.formState.message.$invalid = false;
        this.formState.message.$error = {};
        this.updateError('message');
      }
    },
    async submitForm() {
      if (this.formState.$invalid) {
        this.updateAllErrors();
      } else {
        const feeUpdate = Object.assign({
          id: this.client.id,
          fee: this.fee,
          message: this.message,
          isClientPending: isClientPending(this.client),
        });
        try {
          await this.updateFee(feeUpdate);
          this.fee = '';
          this.message = '';
          this.isTextAreaDisabled = true;
          this.$emit('close');
        } catch (exception) {
          _.errorStore.addError();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .fee-form {
    padding-top: 1rem;
  }

  .error-message, .description {
    padding: 0 0.5rem;
  }

  .fee-field {
    padding: 0 0.5rem 1rem;
  }

  .fee-container {
    display: flex;
    align-items: center;
    padding: 0;

    .fee-percentage {
      margin-left: 0.25rem;
    }

    input {
      width: 4rem;
      margin: 0;
    }

    input.error + .fee-percentage {
      color: $neg-500;
    }

    input:active + .fee-percentage,
    input:focus + .fee-percentage {
      color: $black;
    }
  }

  .email-section {
    padding: 0 0.5rem;

    > span {
      display: block;
    }

    .email-content {
      margin: 0.25rem 0 0;
      height: 11rem;
      resize: vertical;
      min-height: 11rem;
      max-height: 30vh;
      min-width: 100%;

      &:disabled {
        background-color: rgba($primary-100, 0.16);
        border-color: transparent;
      }
    }

    .edit-message {
      margin-top: 0.5rem;
    }

    .error {
      margin-top: 0.5rem;
    }
  }

  @media #{$screen-width-small} {
    .error-message, .description {
      padding: 0 1rem;
    }

    .fee-field {
      padding: 0 1rem 1rem;
    }

    .email-section {
      padding: 0 1rem;

      .email-container {
        padding: 0;

        .edit-message {
          margin: 0.5rem 0 1rem;
        }
      }
    }
  }
</style>
